import { useEffect, useRef, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { formatAuPhoneForDisplay, normalizeAuPhone } from "../lib/phone.js";
import { Button, Card, Field, Input, Modal } from "../lib/ui.js";
import { formatDateOnly } from "../lib/datetime.js";
import { todayInZone } from "../lib/zone.js";

interface CustomerRow {
  id: string;
  name: string | null;
  email: string | null;
  /** yyyy-mm-dd */
  date_of_birth: string | null;
  notes: string | null;
  phones: string[];
}

/**
 * Reads/writes `customers` and `customer_phones` directly (not a view): both are already
 * RLS-scoped to business members (customers_rw / customer_phones_rw in
 * supabase/migrations/0001_init.sql), and neither table holds anything AI/model-internal,
 * so there's no curation gap to close here the way there is for calls.
 *
 * Paged: PAGE_SIZE customers at a time in name order, with a search over name, email and phone number, so a business
 * with thousands of customers never loads them all at once.
 */
export const PAGE_SIZE = 50;

/** Characters PostgREST's or() filter syntax treats specially; dropped from the search text. */
function cleanSearch(text: string): string {
  return text.replace(/[,()"\\*%]/g, " ").replace(/\s+/g, " ").trim();
}

/** The digits of a typed phone number as they appear inside a stored +61 number ("0412 345" -> "412345"). */
export function phoneSearchDigits(text: string): string | null {
  const digits = text.replace(/\D/g, "");
  if (digits.length < 3 || digits.length !== text.replace(/[\s+()-]/g, "").length) return null; // not a phone-ish search
  if (digits.startsWith("61")) return digits.slice(2) || null;
  return digits.startsWith("0") ? digits.slice(1) || null : digits;
}

export function Customers() {
  const { activeBusinessId } = useAuth();
  const readOnly = useReadOnly();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [businessState, setBusinessState] = useState<string | null>(null);
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [total, setTotal] = useState(0);
  const latestLoad = useRef(0);

  // Search after a short pause in typing, from the first page (only when the search really changed).
  useEffect(() => {
    const next = cleanSearch(search);
    if (next === appliedSearch) return;
    const t = setTimeout(() => {
      setAppliedSearch(next);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search, appliedSearch]);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase
      .from("businesses")
      .select("state")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data: business }: { data: { state: string | null } | null }) => setBusinessState(business?.state ?? null));
  }, [activeBusinessId]);

  async function load() {
    if (!activeBusinessId) return;
    const loadId = ++latestLoad.current;
    let query = supabase
      .from("customers")
      .select("id, name, email, date_of_birth, notes, customer_phones(e164)", { count: "exact" })
      .eq("business_id", activeBusinessId);
    if (appliedSearch) {
      const filters = [`name.ilike.*${appliedSearch}*`, `email.ilike.*${appliedSearch}*`];
      const digits = phoneSearchDigits(appliedSearch);
      if (digits) {
        const { data: matches } = await supabase
          .from("customer_phones")
          .select("customer_id")
          .eq("business_id", activeBusinessId)
          .ilike("e164", `%${digits}%`)
          .limit(200);
        const ids = [...new Set((matches ?? []).map((m: { customer_id: string }) => m.customer_id))];
        if (ids.length > 0) filters.push(`id.in.(${ids.join(",")})`);
      }
      query = query.or(filters.join(","));
    }
    const { data, count } = await query
      .order("name")
      .order("id")
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    if (loadId !== latestLoad.current) return; // a newer search or page has started
    const list = (data ?? []).map((c: any) => ({
      id: c.id,
      name: c.name,
      email: c.email,
      date_of_birth: c.date_of_birth,
      notes: c.notes,
      phones: (c.customer_phones ?? []).map((p: { e164: string }) => p.e164),
    }));
    setRows(list);
    setTotal(count ?? list.length);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId, page, appliedSearch]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    // Validate the number before creating anything, and store it as E.164 so Caller ID matches it.
    let e164: string | null = null;
    if (phone.trim()) {
      const normalized = normalizeAuPhone(phone, businessState);
      if (!normalized.ok) {
        setError(normalized.error);
        return;
      }
      e164 = normalized.e164;
    }
    setSaving(true);

    const { data: customer, error: custErr } = await supabase
      .from("customers")
      .insert({ business_id: activeBusinessId, name, email: email || null, date_of_birth: dob || null })
      .select()
      .single();

    if (custErr || !customer) {
      setSaving(false);
      setError(custErr?.message ?? "Could not create customer.");
      return;
    }

    if (e164) {
      const { error: phoneErr } = await supabase
        .from("customer_phones")
        .insert({ business_id: activeBusinessId, customer_id: customer.id, e164 });
      if (phoneErr) {
        setSaving(false);
        setError(phoneErr.message);
        return;
      }
    }

    setName("");
    setPhone("");
    setEmail("");
    setDob("");
    setSaving(false);
    await load();
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Customers</h1>

      {!readOnly && <Card style={{ marginBottom: 20, padding: 16 }}>
      <form onSubmit={handleAdd} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", flex: "1 1 180px", minWidth: 0 }}>
          Name
          <input required value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", flex: "1 1 180px", minWidth: 0 }}>
          Phone
          <input value={phone} onChange={(e) => setPhone(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", flex: "1 1 180px", minWidth: 0 }}>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", flex: "0 1 160px", minWidth: 0 }}>
          Date of birth
          <input type="date" value={dob} min="1900-01-01" max={todayInZone()} onChange={(e) => setDob(e.target.value)} style={fieldStyle} />
        </label>
        <Button variant="primary" type="submit" disabled={saving}>
          Add customer
        </Button>
      </form>
      </Card>}
      {error && <p style={{ color: "var(--rc-danger)", marginTop: -16, marginBottom: 16 }}>{error}</p>}

      <div style={{ marginBottom: 12, maxWidth: 360 }}>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone or email"
          aria-label="Search customers"
        />
      </div>

      <div className="rc-table-scroll"><table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Name</th>
            <th style={{ padding: "10px 12px" }}>Phone</th>
            <th style={{ padding: "10px 12px" }}>Email</th>
            <th style={{ padding: "10px 12px" }}>Date of birth</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
              <td data-label="Name" style={{ padding: "10px 12px" }}>{r.name ?? "-"}</td>
              <td data-label="Phone" style={{ padding: "10px 12px" }}>{r.phones.map(formatAuPhoneForDisplay).join(", ") || "-"}</td>
              <td data-label="Email" style={{ padding: "10px 12px" }}>{r.email ?? "-"}</td>
              <td data-label="Date of birth" style={{ padding: "10px 12px" }}>{r.date_of_birth ? formatDateOnly(r.date_of_birth) : "-"}</td>
              <td className="rc-actions" style={{ padding: "10px 12px" }}>
                {!readOnly && (
                  <button onClick={() => setEditing(r)} style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 14 }}>
                    Edit
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                {appliedSearch ? "No customers match that search." : "No customers yet."}
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
      {total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>
            Showing {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
              Previous
            </Button>
            <Button type="button" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total}>
              Next
            </Button>
          </div>
        </div>
      )}
      {editing && (
        <EditCustomerModal
          customer={editing}
          businessId={activeBusinessId!}
          businessState={businessState}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Edit a customer's name, email, date of birth and phone numbers. Numbers are stored as E.164 (what Caller ID
 *  matches against); a number can belong to only one customer per business. */
function EditCustomerModal({
  customer,
  businessId,
  businessState,
  onClose,
  onSaved,
}: {
  customer: CustomerRow;
  businessId: string;
  businessState: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(customer.name ?? "");
  const [email, setEmail] = useState(customer.email ?? "");
  const [dob, setDob] = useState(customer.date_of_birth ?? "");
  const [phones, setPhones] = useState<string[]>(customer.phones.map(formatAuPhoneForDisplay));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function setPhoneAt(i: number, value: string) {
    setPhones((prev) => prev.map((p, j) => (j === i ? value : p)));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Validate every number before changing anything.
    const wanted: string[] = [];
    for (const raw of phones) {
      if (!raw.trim()) continue;
      const normalized = normalizeAuPhone(raw, businessState);
      if (!normalized.ok) {
        setError(`${raw}: ${normalized.error}`);
        return;
      }
      if (!wanted.includes(normalized.e164)) wanted.push(normalized.e164);
    }
    const toAdd = wanted.filter((n) => !customer.phones.includes(n));
    const toRemove = customer.phones.filter((n) => !wanted.includes(n));

    setSaving(true);
    // New numbers first: a number that already belongs to another customer fails here, before anything else is touched.
    if (toAdd.length > 0) {
      const { error: addErr } = await supabase
        .from("customer_phones")
        .insert(toAdd.map((e164) => ({ business_id: businessId, customer_id: customer.id, e164 })));
      if (addErr) {
        setSaving(false);
        setError(addErr.code === "23505" ? "One of those numbers already belongs to another customer." : addErr.message);
        return;
      }
    }
    const { error: updErr } = await supabase
      .from("customers")
      .update({ name: name.trim() || null, email: email.trim() || null, date_of_birth: dob || null })
      .eq("id", customer.id);
    if (updErr) {
      setSaving(false);
      setError(updErr.message);
      return;
    }
    if (toRemove.length > 0) {
      const { error: delErr } = await supabase
        .from("customer_phones")
        .delete()
        .eq("customer_id", customer.id)
        .in("e164", toRemove);
      if (delErr) {
        setSaving(false);
        setError(delErr.message);
        return;
      }
    }
    setSaving(false);
    onSaved();
  }

  return (
    <Modal title="Edit customer" onClose={onClose}>
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Date of birth">
          <Input type="date" value={dob} min="1900-01-01" max={todayInZone()} onChange={(e) => setDob(e.target.value)} />
        </Field>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--rc-text-muted)" }}>Phone numbers</div>
          {phones.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 8 }}>
              <Input value={p} onChange={(e) => setPhoneAt(i, e.target.value)} aria-label={`Phone ${i + 1}`} />
              <Button type="button" variant="ghost" onClick={() => setPhones((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove phone ${i + 1}`}>
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button type="button" variant="secondary" onClick={() => setPhones((prev) => [...prev, ""])}>
              + Add number
            </Button>
          </div>
        </div>
        {error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: 0 }}>{error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const fieldStyle: React.CSSProperties = { display: "block", marginTop: 4, width: "100%", fontWeight: 400, color: "var(--rc-text)" };
